import { useState } from 'react'
import BulkActionsBar from '../components/BulkActionsBar'
import PaginationControls, { paginateItems } from '../components/PaginationControls'
import { useApp } from '../context/AppContext'
import { persistAppendOnlyVersion, persistDeletedVersion } from '../lib/lumiAppendOnly'
import { gerarCodigo } from '../utils'
import type { CategoriaOrcamentaria } from '../types'

function nowISO() {
  return new Date().toISOString()
}

const GRUPOS = ['MAO_DE_OBRA', 'MATERIAL', 'EQUIPAMENTO', 'SERVICO_TERCEIRIZADO', 'CUSTO_INDIRETO', 'ADMINISTRATIVO']
const NATUREZAS = ['CUSTO', 'DESPESA', 'RECEITA']

const empty = { nome: '', grupo_principal: 'MATERIAL' as CategoriaOrcamentaria['grupo_principal'], natureza: 'CUSTO' as CategoriaOrcamentaria['natureza'], aceita_requisicao: true, aceita_rateio: false, status: 'ATIVA' as CategoriaOrcamentaria['status'], observacoes: '' }

export default function Categorias() {
  const { categorias, setCategorias, addLog } = useApp()
  const [search, setSearch] = useState('')
  const [modal, setModal] = useState(false)
  const [editing, setEditing] = useState<CategoriaOrcamentaria | null>(null)
  const [form, setForm] = useState({ ...empty })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [toast, setToast] = useState('')
  const [saving, setSaving] = useState(false)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  const filtered = categorias.filter(c =>
    c.nome.toLowerCase().includes(search.toLowerCase()) ||
    c.grupo_principal.toLowerCase().includes(search.toLowerCase())
  )
  const paginated = paginateItems(filtered, page, pageSize)

  function showToast(msg: string) { setToast(msg); setTimeout(() => setToast(''), 3000) }

  function openNew() { setEditing(null); setForm({ ...empty }); setErrors({}); setModal(true) }
  function openEdit(c: CategoriaOrcamentaria) {
    setEditing(c)
    setForm({ nome: c.nome, grupo_principal: c.grupo_principal, natureza: c.natureza, aceita_requisicao: c.aceita_requisicao, aceita_rateio: c.aceita_rateio, status: c.status, observacoes: c.observacoes || '' })
    setErrors({}); setModal(true)
  }

  function validate() {
    const e: Record<string, string> = {}
    if (!form.nome.trim()) e.nome = 'Nome obrigatório'
    return e
  }

  async function save() {
    const e = validate()
    if (Object.keys(e).length) { setErrors(e); return }
    setSaving(true)
    try {
      if (editing) {
        const atualizada = { ...editing, ...form, updatedAt: nowISO() } as CategoriaOrcamentaria
        await persistAppendOnlyVersion('categorias_orcamentarias', atualizada as unknown as Record<string, unknown>)
        setCategorias(prev => prev.map(c => c.id === editing.id ? atualizada : c))
        addLog('CATEGORIA', editing.id, 'EDITADO', `Categoria ${form.nome} editada`)
        showToast('Categoria atualizada')
      } else {
        const nova: CategoriaOrcamentaria = { id: 'cat' + Date.now(), codigo: gerarCodigo(), ...form, createdAt: nowISO() }
        await persistAppendOnlyVersion('categorias_orcamentarias', nova as unknown as Record<string, unknown>)
        setCategorias(prev => [...prev, nova])
        addLog('CATEGORIA', nova.id, 'CRIADO', `Categoria ${form.nome} criada`)
        showToast('Categoria cadastrada')
      }
      setModal(false)
    } catch {
      showToast('Erro, tente novamente mais tarde.')
    } finally {
      setSaving(false)
    }
  }

  async function deleteCategoria(categoria: CategoriaOrcamentaria) {
    const confirmed = window.confirm(`Excluir a categoria ${categoria.nome}? Esta acao nao pode ser desfeita.`)
    if (!confirmed) return

    setSaving(true)
    try {
      const dbId = (categoria as any)._id ?? categoria.id
      await persistDeletedVersion('categorias_orcamentarias', categoria as unknown as Record<string, unknown>)
      setCategorias(prev => prev.filter(item => item.id !== categoria.id && (item as any)._id !== dbId))
      addLog('CATEGORIA', categoria.id, 'CANCELADO', `Categoria ${categoria.nome} excluida`)
      showToast('Categoria excluida')
    } catch {
      showToast('Erro, tente novamente mais tarde.')
    } finally {
      setSaving(false)
    }
  }

  async function deleteSelectedCategorias() {
    const selected = filtered.filter(categoria => selectedIds.includes(categoria.id))
    if (selected.length === 0) return
    const confirmed = window.confirm(`Excluir ${selected.length} categoria(s) selecionada(s)?`)
    if (!confirmed) return

    setSaving(true)
    try {
      await Promise.all(selected.map(categoria => persistDeletedVersion('categorias_orcamentarias', categoria as unknown as Record<string, unknown>)))
      const ids = new Set(selected.map(categoria => categoria.id))
      setCategorias(prev => prev.filter(categoria => !ids.has(categoria.id)))
      selected.forEach(categoria => addLog('CATEGORIA', categoria.id, 'CANCELADO', `Categoria ${categoria.nome} excluida`))
      setSelectedIds([])
      showToast('Categorias excluidas')
    } catch {
      showToast('Erro, tente novamente mais tarde.')
    } finally {
      setSaving(false)
    }
  }

  const grupoColor: Record<string, string> = {
    MAO_DE_OBRA: '#a78bfa', MATERIAL: '#60a5fa', EQUIPAMENTO: '#fbbf24',
    SERVICO_TERCEIRIZADO: '#34d399', CUSTO_INDIRETO: '#f87171', ADMINISTRATIVO: '#94a3b8'
  }

  return (
    <div>
      {toast && <div style={{ position: 'fixed', top: 20, right: 24, background: '#22c55e', color: '#fff', padding: '10px 18px', borderRadius: 8, fontSize: 13, zIndex: 9999, fontWeight: 500 }}>{toast}</div>}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h1 style={{ color: '#f7f8f8', fontSize: 18, fontWeight: 600, margin: 0 }}>Categorias Orçamentárias</h1>
          <p style={{ color: '#62666d', fontSize: 13, margin: '2px 0 0' }}>{categorias.length} categorias cadastradas</p>
        </div>
        <button onClick={openNew} style={{ background: '#f0403b', color: '#fff', border: 'none', borderRadius: 7, padding: '8px 16px', fontSize: 13, cursor: 'pointer', fontWeight: 500 }}>+ Nova Categoria</button>
      </div>

      <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); setSelectedIds([]) }} placeholder="Buscar categoria..." style={{ background: '#1c1d24', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 7, padding: '8px 12px', color: '#f7f8f8', fontSize: 13, width: 300, outline: 'none', marginBottom: 16 }} />

      <BulkActionsBar selectedCount={selectedIds.length} onClear={() => setSelectedIds([])} onDelete={() => void deleteSelectedCategorias()} disabled={saving} />

      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#62666d' }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>ðŸ·ï¸</div>
          <p style={{ fontSize: 14 }}>Nenhuma categoria encontrada</p>
          <button onClick={openNew} style={{ marginTop: 12, background: '#1c1d24', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 7, padding: '8px 16px', color: '#ffb4b1', fontSize: 13, cursor: 'pointer' }}>Cadastrar Categoria</button>
        </div>
      ) : (
        <>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <th style={{ width: 32, padding: '8px 12px' }}>
                <input
                  type="checkbox"
                  checked={paginated.items.length > 0 && paginated.items.every(c => selectedIds.includes(c.id))}
                  onChange={event => {
                    const pageIds = paginated.items.map(c => c.id)
                    setSelectedIds(prev => event.target.checked
                      ? Array.from(new Set([...prev, ...pageIds]))
                      : prev.filter(id => !pageIds.includes(id)))
                  }}
                />
              </th>
              {['Código', 'Nome', 'Grupo', 'Natureza', 'Req.', 'Rateio', 'Status', 'Ações'].map(h => (
                <th key={h} style={{ textAlign: 'left', padding: '8px 12px', color: '#62666d', fontWeight: 500 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginated.items.map(c => (
              <tr key={c.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }} className="table-row">
                <td style={{ padding: '10px 12px' }}>
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(c.id)}
                    onChange={event => setSelectedIds(prev => event.target.checked ? [...prev, c.id] : prev.filter(id => id !== c.id))}
                  />
                </td>
                <td style={{ padding: '10px 12px', color: '#a0a4ab', fontFamily: 'monospace', fontSize: 11 }}>{c.codigo}</td>
                <td style={{ padding: '10px 12px', color: '#f7f8f8', fontWeight: 500 }}>{c.nome}</td>
                <td style={{ padding: '10px 12px' }}>
                  <span style={{ background: (grupoColor[c.grupo_principal] || '#94a3b8') + '22', color: grupoColor[c.grupo_principal] || '#94a3b8', borderRadius: 5, padding: '2px 7px', fontSize: 11, fontWeight: 600 }}>{c.grupo_principal.replace(/_/g, ' ')}</span>
                </td>
                <td style={{ padding: '10px 12px', color: '#a0a4ab' }}>{c.natureza}</td>
                <td style={{ padding: '10px 12px', color: c.aceita_requisicao ? '#4ade80' : '#62666d' }}>{c.aceita_requisicao ? 'Sim' : 'Não'}</td>
                <td style={{ padding: '10px 12px', color: c.aceita_rateio ? '#4ade80' : '#62666d' }}>{c.aceita_rateio ? 'Sim' : 'Não'}</td>
                <td style={{ padding: '10px 12px' }}>
                  <span style={{ background: c.status === 'ATIVA' ? '#4ade8022' : '#f8717122', color: c.status === 'ATIVA' ? '#4ade80' : '#f87171', borderRadius: 5, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>{c.status}</span>
                </td>
                <td style={{ padding: '10px 12px' }}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => openEdit(c)} style={{ background: '#1c1d24', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 5, padding: '4px 10px', color: '#ffb4b1', fontSize: 12, cursor: 'pointer' }}>Editar</button>
                    <button onClick={() => deleteCategoria(c)} disabled={saving} style={{ background: '#1c1d24', border: '1px solid rgba(248,113,113,0.25)', borderRadius: 5, padding: '4px 10px', color: '#f87171', fontSize: 12, cursor: 'pointer' }}>Excluir</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <PaginationControls
          page={paginated.safePage}
          pageSize={pageSize}
          totalItems={filtered.length}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </>
          )}

      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#16171e', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: 28, width: 480 }}>
            <h2 style={{ color: '#f7f8f8', fontSize: 16, fontWeight: 600, margin: '0 0 20px' }}>{editing ? 'Editar Categoria' : 'Nova Categoria'}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div style={{ gridColumn: 'span 2' }}>
                <label htmlFor="categoria-nome" style={{ color: '#a0a4ab', fontSize: 12, display: 'block', marginBottom: 4 }}>Nome *</label>
                <input id="categoria-nome" value={form.nome} onChange={e => setForm(p => ({ ...p, nome: e.target.value }))} style={{ width: '100%', background: '#1c1d24', border: `1px solid ${errors.nome ? '#f87171' : 'rgba(255,255,255,0.08)'}`, borderRadius: 6, padding: '7px 10px', color: '#f7f8f8', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                {errors.nome && <p style={{ color: '#f87171', fontSize: 11, margin: '3px 0 0' }}>{errors.nome}</p>}
              </div>
              <div>
                <label htmlFor="categoria-grupo" style={{ color: '#a0a4ab', fontSize: 12, display: 'block', marginBottom: 4 }}>Grupo Principal</label>
                <select id="categoria-grupo" value={form.grupo_principal} onChange={e => setForm(p => ({ ...p, grupo_principal: e.target.value as any }))} style={{ width: '100%', background: '#1c1d24', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, padding: '7px 10px', color: '#f7f8f8', fontSize: 13, outline: 'none' }}>
                  {GRUPOS.map(g => <option key={g} value={g}>{g.replace(/_/g, ' ')}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="categoria-natureza" style={{ color: '#a0a4ab', fontSize: 12, display: 'block', marginBottom: 4 }}>Natureza</label>
                <select id="categoria-natureza" value={form.natureza} onChange={e => setForm(p => ({ ...p, natureza: e.target.value as any }))} style={{ width: '100%', background: '#1c1d24', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, padding: '7px 10px', color: '#f7f8f8', fontSize: 13, outline: 'none' }}>
                  {NATUREZAS.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input type="checkbox" id="req" checked={form.aceita_requisicao} onChange={e => setForm(p => ({ ...p, aceita_requisicao: e.target.checked }))} style={{ accentColor: '#f0403b' }} />
                <label htmlFor="req" style={{ color: '#a0a4ab', fontSize: 13, cursor: 'pointer' }}>Aceita Requisição</label>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input type="checkbox" id="rat" checked={form.aceita_rateio} onChange={e => setForm(p => ({ ...p, aceita_rateio: e.target.checked }))} style={{ accentColor: '#f0403b' }} />
                <label htmlFor="rat" style={{ color: '#a0a4ab', fontSize: 13, cursor: 'pointer' }}>Aceita Rateio</label>
              </div>
              <div>
                <label htmlFor="categoria-status" style={{ color: '#a0a4ab', fontSize: 12, display: 'block', marginBottom: 4 }}>Status</label>
                <select id="categoria-status" value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value as any }))} style={{ width: '100%', background: '#1c1d24', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, padding: '7px 10px', color: '#f7f8f8', fontSize: 13, outline: 'none' }}>
                  <option value="ATIVA">ATIVA</option>
                  <option value="INATIVA">INATIVA</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 24 }}>
              <button onClick={() => setModal(false)} style={{ background: '#1c1d24', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 7, padding: '8px 18px', color: '#a0a4ab', fontSize: 13, cursor: 'pointer' }}>Cancelar</button>
              <button onClick={save} disabled={saving} style={{ background: '#f0403b', border: 'none', borderRadius: 7, padding: '8px 18px', color: '#fff', fontSize: 13, cursor: 'pointer', fontWeight: 500 }}>{saving ? 'Salvando...' : 'Salvar'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

